import { UploadOutlined } from "@ant-design/icons";
import { Button, notification } from "antd";
import { useCreatePriceListMutation } from "api/priceListApi";
import dayjs from "dayjs";
import { ChangeEvent, useRef, useState } from "react";
import { PriceListResponse } from "types/price-list.type";
import * as XLSX from "xlsx";

const LOCAL_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";

const buildExcelDateTime = (year: number, month: number, day: number, hour = 0, minute = 0, second = 0) => dayjs(new Date(year, month - 1, day, hour, minute, second)).format(LOCAL_DATE_TIME_FORMAT);

const parseExcelDateTime = (value: unknown) => {
	if (!value) return dayjs().format(LOCAL_DATE_TIME_FORMAT);

	if (typeof value === "number") {
		const parsed = XLSX.SSF.parse_date_code(value);

		if (parsed) {
			const totalSeconds = Math.round((parsed.H || 0) * 3600 + (parsed.M || 0) * 60 + (parsed.S || 0));
			const roundedMinutes = Math.round(totalSeconds / 60);
			const hour = Math.floor(roundedMinutes / 60);
			const minute = roundedMinutes % 60;

			return buildExcelDateTime(parsed.y, parsed.m, parsed.d, hour, minute, 0);
		}
	}

	const str = String(value).trim();

	const dateTimeMatch = str.match(/^(\d{1,4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);

	if (dateTimeMatch) {
		const [, part1, part2, part3, hour = "0", minute = "0", second = "0"] = dateTimeMatch;
		const isYearFirst = part1.length === 4;
		const rawYear = isYearFirst ? Number(part1) : Number(part3);
		const year = rawYear < 100 ? 2000 + rawYear : rawYear;
		const day = isYearFirst ? Number(part3) : Number(part1);
		const month = Number(part2);

		return buildExcelDateTime(year, month, day, Number(hour), Number(minute), Number(second));
	}

	return dayjs(str).second(0).millisecond(0).format(LOCAL_DATE_TIME_FORMAT);
};

const ImportExcelPriceListButton = ({ disabled = false, setFailed }: { disabled?: boolean; setFailed?: (failed: string[]) => void }) => {
	const [createPriceList] = useCreatePriceListMutation();
	const inputRef = useRef<HTMLInputElement>(null);

	const [spin, setSpin] = useState(false);

	const onChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSpin(true);
		const file = event.target.files?.[0];

		if (file) {
			setFailed?.([]);
			importExcel(file, [
				"stt",
				"priceListCode",
				"customerCode",
				"customerName",
				"farmCode",
				"farmName",
				"productCode",
				"productName",
				"chickenType",
				"gender",
				"uom",
				"priceAmount",
				"currency",
				"validFrom",
				"validTo",
			]).finally(() => {
				event.target.value = "";
			});
			return;
		}

		setSpin(false);
		event.target.value = "";
	};

	const importExcel = async (file: File, header: string[]) => {
		const fileReader = new FileReader();

		return new Promise<void>((resolve) => {
			fileReader.onload = async (event: ProgressEvent<FileReader>) => {
				try {
					const result = event.target?.result;
					const workbook = XLSX.read(result, { type: "binary", cellDates: false, cellText: false });
					const sheetNameList = Object.keys(workbook.Sheets);
					const sheetOne = sheetNameList.length ? sheetNameList[0] : "Sheet1";
					const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetOne], { header, raw: true, defval: "" });

					if (!data?.length) return;

					data.shift();

					const newData = (data as PriceListResponse[]).map((item) => ({
						...item,
						chickenType: Number(item.chickenType || 0),
						priceAmount: Number(item.priceAmount || 0),
						validFrom: parseExcelDateTime(item.validFrom),
						validTo: parseExcelDateTime(item.validTo),
						applyAllHouse: false,
					}));
					const importResult = await createPriceList(newData).unwrap();
					if (importResult?.failed?.length) {
						setFailed?.(importResult.failed);
					} else {
						notification.success({
							message: "Success",
						});
					}
				} catch (e: any) {
					notification.error({
						message: e?.data?.message || "Error",
					});
				} finally {
					setSpin(false);
					resolve();
				}
			};
			fileReader.readAsBinaryString(file);
		});
	};

	return (
		<>
			<Button
				disabled={disabled}
				loading={spin}
				type="primary"
				ghost
				icon={<UploadOutlined />}
				onClick={() => {
					inputRef.current?.click();
				}}>
				Import
			</Button>
			<input ref={inputRef} style={{ display: "none" }} type="file" accept=".xlsx, .xls" onChange={onChange} />
		</>
	);
};

export default ImportExcelPriceListButton;
